import { UserRole } from 'models/enum';
import { ChartActionsComponentProps, ChartMetaFormField, ContentItem } from 'models/ui';
import { Box, ChartPermission, ErrorCard, ErrorFormField, SectionDivider } from 'components/common';
import { ELSButton, ELSFlex, ELSFlexItem } from 'components/common/els';
import { DropdownAction, FormField, FormFieldLabel, Placeholder, SectionTitle } from './constants';
import PupilSizeDropdown from './PupilSizeDropdown';
import './neurological.evaluation.scss';

export interface NeurologicalEvaluationViewProps {
  chartActionsProps: ChartActionsComponentProps;
  formFieldMap: Map<string, ChartMetaFormField>;
  formSubmittedCount: number;
  isAuthor?: boolean;
  userRole?: string;
}

const PLACEHOLDER_OPTION_ID = '__placeholder__';

const buildPlaceholderItem = (placeholder: string): ContentItem =>
  ({
    id: PLACEHOLDER_OPTION_ID,
    name: placeholder,
    label: placeholder,
    value: '',
    selected: false,
    isActive: true,
    isHidden: false,
    hidden: true,
    disabled: true,
    displayOrder: -1,
    onChange: () => undefined
  } as unknown as ContentItem);

const buildClearItem = (): ContentItem =>
  ({
    id: DropdownAction.CLEAR_OPTION_ID,
    name: DropdownAction.CLEAR_LABEL,
    label: DropdownAction.CLEAR_LABEL,
    value: '',
    selected: false,
    isActive: true,
    isHidden: false,
    displayOrder: 0,
    onChange: () => undefined
  } as unknown as ContentItem);

const isSyntheticDefaultItem = (item: ContentItem): boolean => item.id === '' && item.value === '';

const dropdownWrapperClass = (formField: ChartMetaFormField | undefined): string | undefined => {
  const value = String(formField?.value ?? '').trim();
  return value ? undefined : 'neurological-evaluation__dropdown--placeholder';
};

const withDropdownPlaceholder = (formField: ChartMetaFormField | undefined, placeholder: string): ChartMetaFormField | undefined => {
  if (!formField) return formField;
  const existing = formField.chartContent ?? [];
  if (existing.some((item) => item.id === PLACEHOLDER_OPTION_ID)) {
    return formField;
  }
  const realOptions = existing.filter((item) => !isSyntheticDefaultItem(item));
  return { ...formField, chartContent: [buildPlaceholderItem(placeholder), buildClearItem(), ...realOptions] };
};

const NeurologicalEvaluationView = (props: NeurologicalEvaluationViewProps) => {
  const { chartActionsProps, formFieldMap, formSubmittedCount, isAuthor, userRole } = props;
  const { enableSaveButton, enableDisplayRecordsButton, onSaveClick, onCancelClick, onDisplayRecordsClick } = chartActionsProps;

  const isFacultyViewer = !isAuthor && userRole === UserRole.INSTRUCTOR;

  const renderChartActions = ({ allowSaveFragment }: { allowSaveFragment?: boolean }) => {
    if (!allowSaveFragment || isFacultyViewer) return <></>;
    return (
      <div className="neurological-evaluation__actions">
        <ELSFlex className="sng-form-buttons" left wrap>
          {isAuthor ? (
            <>
              <ELSFlexItem>
                <ELSButton type="primary" text="Save Records" isDisabled={!enableSaveButton} onClick={onSaveClick} />
              </ELSFlexItem>
              <ELSFlexItem>
                <ELSButton type="secondary" text="Return with data" isDisabled={!enableDisplayRecordsButton} onClick={onDisplayRecordsClick} />
              </ELSFlexItem>
            </>
          ) : (
            <>
              <ELSFlexItem>
                <ELSButton type="primary" text="Save & Complete" iconName="chevron-right" iconPosition="right" iconSize="1x" isDisabled={!enableSaveButton} onClick={onSaveClick} />
              </ELSFlexItem>
              <ELSFlexItem>
                <ELSButton type="secondary" text="Discard Changes" onClick={onCancelClick} />
              </ELSFlexItem>
            </>
          )}
        </ELSFlex>
      </div>
    );
  };

  return (
    <div className="neurological-evaluation">
      <h1>{SectionTitle.NEUROLOGICAL_EVALUATION}</h1>
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />

      <SectionDivider />
      <Box mt2>
        <h2>{SectionTitle.PUPILS}</h2>
        <ELSFlex className="u-flex-gutter-3x" wrap left>
          <ELSFlexItem w="1o2" md="1o1">
            <PupilSizeDropdown
              id={FormField.LEFT_PUPIL_SIZE}
              label={FormFieldLabel.LEFT_PUPIL_SIZE}
              placeholder={Placeholder.PUPIL_SIZE}
              sizeFormField={formFieldMap.get(FormField.LEFT_PUPIL_SIZE)}
              roundFormField={formFieldMap.get(FormField.LEFT_PUPIL)}
            />
          </ELSFlexItem>
          <ELSFlexItem w="1o2" md="1o1">
            <PupilSizeDropdown
              id={FormField.RIGHT_PUPIL_SIZE}
              label={FormFieldLabel.RIGHT_PUPIL_SIZE}
              placeholder={Placeholder.PUPIL_SIZE}
              sizeFormField={formFieldMap.get(FormField.RIGHT_PUPIL_SIZE)}
              roundFormField={formFieldMap.get(FormField.RIGHT_PUPIL)}
            />
          </ELSFlexItem>
        </ELSFlex>
      </Box>

      <SectionDivider />
      <Box mt2>
        <h2>{SectionTitle.PUPIL_REACTION}</h2>
        <ELSFlex className="u-flex-gutter-3x" wrap left>
          <ELSFlexItem w="1o2" md="1o1">
            <div className={dropdownWrapperClass(formFieldMap.get(FormField.LEFT_PUPIL_REACTION))}>
              <ErrorFormField
                className="u-els-margin-top"
                formField={withDropdownPlaceholder(formFieldMap.get(FormField.LEFT_PUPIL_REACTION), Placeholder.PUPIL_REACTION)}
                formSubmittedCount={formSubmittedCount}
              />
            </div>
          </ELSFlexItem>
          <ELSFlexItem w="1o2" md="1o1">
            <div className={dropdownWrapperClass(formFieldMap.get(FormField.RIGHT_PUPIL_REACTION))}>
              <ErrorFormField
                className="u-els-margin-top"
                formField={withDropdownPlaceholder(formFieldMap.get(FormField.RIGHT_PUPIL_REACTION), Placeholder.PUPIL_REACTION)}
                formSubmittedCount={formSubmittedCount}
              />
            </div>
          </ELSFlexItem>
        </ELSFlex>
      </Box>

      <SectionDivider />
      <Box mt2>
        <h2>{SectionTitle.LIMB_MOVEMENT}</h2>
        <ELSFlex className="u-flex-gutter-3x" wrap left>
          <ELSFlexItem w="1o2" md="1o1">
            <div className={dropdownWrapperClass(formFieldMap.get(FormField.ARM_MOVEMENT))}>
              <ErrorFormField
                className="u-els-margin-top"
                formField={withDropdownPlaceholder(formFieldMap.get(FormField.ARM_MOVEMENT), Placeholder.LIMB_MOVEMENT)}
                formSubmittedCount={formSubmittedCount}
              />
            </div>
          </ELSFlexItem>
          <ELSFlexItem w="1o2" md="1o1">
            <div className={dropdownWrapperClass(formFieldMap.get(FormField.LEG_MOVEMENT))}>
              <ErrorFormField
                className="u-els-margin-top"
                formField={withDropdownPlaceholder(formFieldMap.get(FormField.LEG_MOVEMENT), Placeholder.LIMB_MOVEMENT)}
                formSubmittedCount={formSubmittedCount}
              />
            </div>
          </ELSFlexItem>
        </ELSFlex>
      </Box>

      <SectionDivider />
      <Box mt2 className="neurological-evaluation__last-section">
        <h2>{SectionTitle.COMA_STATUS}</h2>
        <ELSFlex className="u-flex-gutter-3x" wrap left>
          <ELSFlexItem w="1o2" md="1o1">
            <div className={dropdownWrapperClass(formFieldMap.get(FormField.COMA_STATUS))}>
              <ErrorFormField
                className="u-els-margin-top"
                formField={withDropdownPlaceholder(formFieldMap.get(FormField.COMA_STATUS), Placeholder.COMA_STATUS)}
                formSubmittedCount={formSubmittedCount}
              />
            </div>
          </ELSFlexItem>
        </ELSFlex>
      </Box>

      <ChartPermission>{renderChartActions}</ChartPermission>
    </div>
  );
};

NeurologicalEvaluationView.displayName = 'NeurologicalEvaluationView';
export default NeurologicalEvaluationView;
